import { useMemo } from 'react';
import { Box, HStack, VStack, Menu, Pressable, useTheme } from 'native-base';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { Text, ZapFastIcon } from '@pimm/base';
import { CleaningTaskStatusEnum } from '@pimm/services/lib/sms-workforce/types';
import { CleaningTaskItem } from '../reducers';

type PopoverAutoAssignMenuProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  tasks: CleaningTaskItem[];
  eligibleForAutoAssign: CleaningTaskItem[];
  onEnterAutoAssignMode: () => void;
  /** Compact icon-only trigger (used in the section header tiles on the main page). */
  compact?: boolean;
};

export const PopoverAutoAssignMenu = ({
  isOpen,
  onOpen,
  onClose,
  tasks,
  eligibleForAutoAssign,
  onEnterAutoAssignMode,
  compact = false,
}: PopoverAutoAssignMenuProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const hasEligible = eligibleForAutoAssign.length > 0;

  const ineligibleReason = useMemo(() => {
    if (hasEligible) return null;
    const activeTasks = tasks.filter(
      task => task.assignee?.status !== CleaningTaskStatusEnum.Complete && task.assignee?.status !== CleaningTaskStatusEnum.NotApplicable,
    );
    const reasons: [boolean, string, string][] = [
      [activeTasks.length === 0, 'all_tasks_are_complete', 'every_task_completed_or_na'],
      [activeTasks.every(task => !task.assignee?.dueDate), 'no_tasks_have_scheduled_time', 'set_time_for_tasks_first'],
      [
        activeTasks.every(task => !!task.assignee?.employeeId),
        'all_tasks_already_have_employees',
        'every_eligible_task_already_has_employee',
      ],
    ];
    const [, titleKey, bodyKey] = reasons.find(([condition]) => condition) ?? [
      true,
      'no_eligible_tasks',
      'auto_assign_must_be_scheduled_unassigned',
    ];
    return { title: t(`cleaning_task:${titleKey}`), body: t(`cleaning_task:${bodyKey}`) };
  }, [hasEligible, tasks, t]);

  const handlePress = () => {
    if (hasEligible) {
      onEnterAutoAssignMode();
    } else {
      onOpen();
    }
  };

  return (
    <Menu
      isOpen={!hasEligible && isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom right"
      p={0}
      mt={3}
      borderRadius="xl"
      overflow="hidden"
      trigger={triggerProps =>
        compact ? (
          <Pressable {...triggerProps} onPress={handlePress}>
            <HStack
              h="30px"
              px={2}
              space={1.5}
              alignItems="center"
              bg={hasEligible ? 'primary.500' : 'gray.100'}
              borderRadius={6}
            >
              <ZapFastIcon size="sm" color={hasEligible ? 'gray.900' : 'gray.500'} />
              <Text size="sm" fontWeight={600} color={hasEligible ? 'gray.900' : 'gray.500'}>
                {t('cleaning_task:auto_assign')}
              </Text>
            </HStack>
          </Pressable>
        ) : (
          <Pressable {...triggerProps} onPress={handlePress}>
            <HStack
              px={3}
              py={2}
              space={2}
              alignItems="center"
              bg={hasEligible ? 'primary.400' : 'gray.50'}
              borderWidth={1}
              borderRadius="md"
              borderColor={hasEligible ? 'primary.400' : 'gray.200'}
              style={
                isOpen
                  ? {
                      shadowColor: colors.gray[500],
                      shadowOpacity: 0.8,
                      shadowRadius: 8,
                      elevation: 8,
                    }
                  : undefined
              }
            >
              <ZapFastIcon size="md" color={hasEligible ? colors.gray[900] : colors.gray[500]} />
              <Text size="md" fontWeight={600} color={hasEligible ? colors.gray[900] : colors.gray[500]}>
                {t('cleaning_task:auto_assign')}
              </Text>
            </HStack>
          </Pressable>
        )
      }
    >
      {/* Ineligible Reason Content */}
      {!hasEligible && ineligibleReason && (
        <Box p={3} w="260px" bg="white">
          <HStack space={2} alignItems="flex-start">
            <Box
              w={7}
              h={7}
              rounded="md"
              bg={colors.primary[50]}
              borderWidth={1}
              borderColor={colors.primary[100]}
              alignItems="center"
              justifyContent="center"
            >
              <Info size={16} color={colors.primary[700]} />
            </Box>
            <VStack flex={1} space={1}>
              <Text size="md" fontWeight={600} color="gray.900">
                {ineligibleReason.title}
              </Text>
              <Text size="sm" color="gray.700" lineHeight="sm">
                {ineligibleReason.body}
              </Text>
            </VStack>
          </HStack>
        </Box>
      )}
    </Menu>
  );
};

export default PopoverAutoAssignMenu;
